import * as React from "react";
import { useLocation } from "react-router";

import { VisualizerRouteParams } from "@/shared/navigation/types";

import { isTabbedSidebarView, useWorkspaceSidebar } from "../sidebarContext";

interface TopicFilterRequest {
  /** The filter term to apply */
  term: string;

  /**
   * Unique key to know when to apply the filter.
   * This is useful when the same term is used twice,
   * but we want to apply the filter again because the user
   * could have typed something else in between.
   */
  key: number;
}

/**
 * Merges topic filter requests from two sources:
 * 1. Sidebar context (from sidebar.openView - inside visualizer actions)
 * 2. Navigation state (from route navigation - outside visualizer)
 *
 * Precedence: Sidebar context > Navigation state
 *
 * This ensures that the most recent action is always applied:
 * - Inside visualizer: sidebar.openView updates context → context wins
 * - Outside visualizer: navigate() sets state → state used (context undefined)
 * - Both exist: context is more recent → context wins
 *
 * @returns Merged topic filter request from the most recent source
 */
export function useTopicFilterRequest(): TopicFilterRequest | undefined {
  const state = useLocation().state as
    | VisualizerRouteParams["state"]
    | undefined;
  const sidebar = useWorkspaceSidebar();

  return React.useMemo(() => {
    const filterFromSidebar = isTabbedSidebarView(sidebar.activeView)
      ? sidebar.activeView.data?.initialTopicFilter
      : undefined;

    const filterFromNavState = state?.initialTopicFilter;

    // Prefer sidebar context over nav state (sidebar is more recent when inside visualizer)
    return filterFromSidebar || filterFromNavState;
  }, [sidebar.activeView, state]);
}
